import clsx from 'clsx';
import { PiHeartBold, PiHeartFill, PiArrowRightBold } from 'react-icons/pi';
import { useConnectionStore } from '@ewf/stores/connection';
import { useRemoteResourcesStore } from '@ewf/stores/remote-resources';
import { GenericChip } from '@ewf/components/Chips/GenericChip';
import cloudsImage from '@ewf/assets/images/clouds.png';
import windTurbineImage from '@ewf/assets/images/wind-turbine.png';
import officialBadge from '@ewf/assets/icons/official-solution-group.svg';
import { useApiServiceStore } from '@ewf/stores/api';
import { GENERIC_CHIP_COLOR } from '@shared/types/enums';
import { WorkerApi } from '@shared/types/api';
import { useSolutionGroupsStore } from '@ewf/stores/solution-groups';
import { Tooltip } from '@ewf/components/Tooltip';

export const WorkerCard = ({
  id,
  category,
  name,
  description,
  status,
  hasOperatorsAllowlist,
  isOfficial,
  isExpired,
  kycStatus,
}: Props): JSX.Element => {
  const workerApi = useApiServiceStore.getState().api;
  const isConnected = useConnectionStore((state) => state.isConnected);
  const { small: workerCardImages } = useRemoteResourcesStore((state) => state.workerCardImages);
  const setFavourite = useSolutionGroupsStore((state) => state.setFavourite);
  const backgroundImage = workerCardImages[id.toLowerCase()] || windTurbineImage;
  const isFav = Boolean(status?.isFavourites);

  const KYC_STATUS = {
    required: {
      color: GENERIC_CHIP_COLOR.Gray,
      label: 'KYC Required',
    },
    submitted: {
      color: GENERIC_CHIP_COLOR.Blue,
      label: 'KYC Submitted',
    },
    approved: {
      color: GENERIC_CHIP_COLOR.Teal,
      label: 'KYC Verified',
    },
    rejected: {
      color: GENERIC_CHIP_COLOR.Red,
      label: 'KYC Rejected',
    },
    whitelisted: {
      color: GENERIC_CHIP_COLOR.White,
      label: 'KYC Whitelisted',
    },
  };

  const toggleFavorite = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const oldFavState = isFav;
    const newFavState = !isFav;

    try {
      setFavourite(id.toString(), newFavState);
      newFavState === true
        ? await workerApi.addFavourite(id.toString())
        : await workerApi.delFavourite(id.toString());
    } catch (err) {
      console.error(err);
      setFavourite(id.toString(), oldFavState);
    }
  };

  return (
    <div
      style={{ backgroundImage: `url(${cloudsImage})` }}
      className="group relative h-full overflow-hidden rounded-lg bg-cover bg-no-repeat transition-all duration-100 hover:shadow-card"
    >
      <img
        className="absolute bottom-0 right-0 w-full rounded-r-lg"
        src={backgroundImage}
        onError={(e) => (e.currentTarget.src = windTurbineImage)}
        alt="Solution group card"
        referrerPolicy="same-origin"
      />
      <div className="relative h-full rounded-lg p-6 bg-worker-gradient">
        <div className="absolute right-4 top-4 z-20 space-y-2 ">
          <div className="relative flex flex-col items-center gap-2">
            <button
              onClick={toggleFavorite}
              className={clsx({
                'relative flex h-[32px] w-[32px] items-center justify-center rounded-full bg-gray-90 hover:bg-gray-80 active:bg-gray-95':
                  true,
                hidden: !isConnected,
              })}
              aria-label="add to favourites"
            >
              <PiHeartFill
                size={16}
                className={clsx({
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 fill-brand': true,
                  hidden: !isFav,
                })}
              />
              <PiHeartBold
                size={16}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              />
            </button>
            <Tooltip content={<p>This Solution Group is audited and verified by Energy Web</p>}>
              <div
                className={clsx({
                  'grid h-[32px] w-[32px] place-items-center rounded-full bg-gray-90': true,
                  hidden: !isOfficial,
                })}
              >
                <img src={officialBadge} alt="official badge" />
              </div>
            </Tooltip>
          </div>

          <button className="duraion-100 block rounded-full bg-white p-2 opacity-0 transition-all group-hover:opacity-100">
            <PiArrowRightBold size={16} className="text-black" />
          </button>
        </div>
        <div className="space-between relative z-10 flex h-full flex-col">
          <div className="mb-4 h-full">
            {kycStatus && (
              <div className="flex gap-2">
                {(kycStatus === 'idle' || kycStatus === 'submitted') && (
                  <div className="mb-2">
                    <GenericChip
                      label={KYC_STATUS['required']?.label}
                      color={KYC_STATUS['required']?.color}
                    ></GenericChip>
                  </div>
                )}
                {kycStatus !== 'idle' && (
                  <div className="mb-2">
                    <GenericChip
                      label={KYC_STATUS[kycStatus as keyof typeof KYC_STATUS]?.label}
                      color={KYC_STATUS[kycStatus as keyof typeof KYC_STATUS]?.color}
                    ></GenericChip>
                  </div>
                )}
              </div>
            )}

            <p className="mb-2 text-sm uppercase">{category}</p>
            <p
              className="mb-2 max-w-[220px] font-primary-bold text-[22px] "
              aria-label="solution-group-name"
            >
              {name}
            </p>
            <p className="mb-2 max-w-[200px] text-sm">{description}</p>
            <div className="flex gap-2">
              {hasOperatorsAllowlist && (
                <GenericChip label="PRIVATE" color={GENERIC_CHIP_COLOR.White}></GenericChip>
              )}
              {isExpired && (
                <GenericChip label="EXPIRED" color={GENERIC_CHIP_COLOR.White}></GenericChip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface Props {
  id: WorkerApi.WorkerId;
  category?: string | null;
  name?: string | null;
  description?: string | null;
  progress?: undefined | number;
  hasOperatorsAllowlist?: boolean;
  isOfficial?: boolean;
  isExpired?: boolean;
  kycStatus?: string;
  status?: {
    isFavourites: boolean;
  };
}
